import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import moment from 'moment';
import { MilestoneStore } from 'store/planning';

interface TimelineMilestone {
    id: number;
    name: string;
    status: string;
    type: string;
    start_date: string | null;
    deadline: string | null;
}

interface TimelineProject {
    id: number;
    name: string;
    company_name: string | null;
    milestones: TimelineMilestone[];
}

interface Props {
    milestoneStore: MilestoneStore;
}

const STATUS_COLORS: Record<string, string> = {
    planning: '#767676',
    active: '#2185d0',
    completed: '#21ba45',
    on_hold: '#fbbf24',
};

const STATUS_LABELS: Record<string, string> = {
    planning: 'Planning',
    active: 'Active',
    completed: 'Completed',
    on_hold: 'On Hold',
};

@observer
export class ProjectTimelineTab extends Component<Props> {
    @observable startDate: string;
    @observable endDate: string;
    @observable projects: TimelineProject[] = [];
    @observable loading = false;
    @observable error: string | null = null;

    constructor(props: Props) {
        super(props);
        const now = moment();
        this.startDate = now.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        this.endDate = now.clone().add(1, 'month').endOf('month').format('YYYY-MM-DD');
    }

    async componentDidMount() {
        await this.loadTimeline();
    }

    @action
    setStartDate = (value: string) => {
        this.startDate = value;
    };

    @action
    setEndDate = (value: string) => {
        this.endDate = value;
    };

    @action
    loadTimeline = async () => {
        this.loading = true;
        this.error = null;
        try {
            const api = (this.props.milestoneStore as any).api;
            const res = await api.get('subproject/project_timeline/', {
                start_date: this.startDate,
                end_date: this.endDate,
            });
            const data = res.data || res;
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            this.projects = (parsed.projects || parsed || []) as TimelineProject[];
        } catch (e) {
            console.error('Failed to load project timeline:', e);
            this.error = 'Failed to load timeline data.';
            this.projects = [];
        } finally {
            this.loading = false;
        }
    };

    @action
    applyFilter = () => {
        this.loadTimeline();
    };

    getProjectLabel = (project: TimelineProject): string => {
        if (project.company_name) {
            return project.company_name + ' - ' + project.name;
        }
        return project.name;
    };

    renderTimelineMilestone = (milestone: TimelineMilestone, rangeStart: moment.Moment, rangeEnd: moment.Moment, rangeDays: number) => {
        const msStart = milestone.start_date ? moment(milestone.start_date) : null;
        const msEnd = milestone.deadline ? moment(milestone.deadline) : null;

        let effectiveStart = rangeStart;
        if (msStart && !msStart.isBefore(rangeStart)) {
            effectiveStart = msStart;
        }
        let effectiveEnd = rangeEnd;
        if (msEnd && !msEnd.isAfter(rangeEnd)) {
            effectiveEnd = msEnd;
        }

        const startOffset = effectiveStart.diff(rangeStart, 'days');
        const durationDays = Math.max(1, effectiveEnd.diff(effectiveStart, 'days') + 1);

        const leftPct = (startOffset / rangeDays) * 100;
        const widthPct = (durationDays / rangeDays) * 100;

        const color = STATUS_COLORS[milestone.status] || '#767676';
        const label = milestone.name + ' (' + (STATUS_LABELS[milestone.status] || milestone.status) + ')';

        return (
            <div
                key={milestone.id}
                className="sp-timeline-milestone-bar"
                style={{
                    left: leftPct + '%',
                    width: widthPct + '%',
                    background: color,
                }}
                title={label}
                data-test-timeline-milestone={milestone.id}
            >
                <span className="sp-timeline-milestone-label">{milestone.name}</span>
            </div>
        );
    };

    renderProject = (project: TimelineProject, rangeStart: moment.Moment, rangeEnd: moment.Moment, rangeDays: number) => {
        return (
            <div className="sp-timeline-project" key={project.id} data-test-timeline-project={project.id}>
                <div className="sp-timeline-project-header">
                    {this.getProjectLabel(project)}
                    <span className="sp-timeline-milestone-count">
                        {project.milestones.length} milestone{project.milestones.length !== 1 ? 's' : ''}
                    </span>
                </div>
                <div className="sp-timeline-track">
                    {project.milestones.map(m => this.renderTimelineMilestone(m, rangeStart, rangeEnd, rangeDays))}
                </div>
            </div>
        );
    };

    renderDateAxis = (rangeStart: moment.Moment, rangeEnd: moment.Moment, rangeDays: number) => {
        const markers: { label: string; pct: number }[] = [];
        const cursor = rangeStart.clone().startOf('month');
        while (cursor.isBefore(rangeEnd) || cursor.isSame(rangeEnd, 'month')) {
            const offset = cursor.diff(rangeStart, 'days');
            const pct = (offset / rangeDays) * 100;
            if (pct >= 0 && pct <= 100) {
                markers.push({ label: cursor.format('MMM YYYY'), pct: pct });
            }
            cursor.add(1, 'month');
        }
        return (
            <div className="sp-timeline-axis">
                {markers.map((m, i) => (
                    <div
                        key={i}
                        className="sp-timeline-axis-marker"
                        style={{ left: m.pct + '%' }}
                    >
                        {m.label}
                    </div>
                ))}
            </div>
        );
    };

    render() {
        const rangeStart = moment(this.startDate);
        const rangeEnd = moment(this.endDate);
        const rangeDays = Math.max(1, rangeEnd.diff(rangeStart, 'days') + 1);

        let body: React.ReactNode;
        if (this.loading) {
            body = <div className="sp-loading">Loading timeline...</div>;
        } else if (this.projects.length === 0) {
            body = (
                <div className="sp-empty-state">
                    <h3>No Projects with Milestones</h3>
                    <p>No milestones fall within the selected date range.</p>
                </div>
            );
        } else {
            body = (
                <div className="sp-timeline-body">
                    {this.renderDateAxis(rangeStart, rangeEnd, rangeDays)}
                    {this.projects.map(p => this.renderProject(p, rangeStart, rangeEnd, rangeDays))}
                </div>
            );
        }

        return (
            <div className="sp-timeline-container" data-test-project-timeline-tab>
                <div className="sp-timeline-filters">
                    <div className="sp-form-group sp-timeline-filter-group">
                        <label>Start Date</label>
                        <input
                            className="sp-form-input"
                            type="date"
                            value={this.startDate}
                            data-test-timeline-start-date
                            onChange={(e) => this.setStartDate(e.target.value)}
                        />
                    </div>
                    <div className="sp-form-group sp-timeline-filter-group">
                        <label>End Date</label>
                        <input
                            className="sp-form-input"
                            type="date"
                            value={this.endDate}
                            data-test-timeline-end-date
                            onChange={(e) => this.setEndDate(e.target.value)}
                        />
                    </div>
                    <button
                        className="sp-btn sp-btn-primary"
                        onClick={this.applyFilter}
                        data-test-timeline-apply
                    >
                        Apply
                    </button>
                </div>

                <div className="sp-timeline-legend">
                    {Object.keys(STATUS_COLORS).map(status => (
                        <div className="sp-timeline-legend-item" key={status}>
                            <span className="sp-timeline-legend-dot" style={{ background: STATUS_COLORS[status] }} />
                            {STATUS_LABELS[status] || status}
                        </div>
                    ))}
                </div>

                {this.error && <div className="sp-error">{this.error}</div>}
                {body}
            </div>
        );
    }
}

export default ProjectTimelineTab;
